import { useState, useEffect, type FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { signUp, signIn, confirmSignUp, signOut } from 'aws-amplify/auth';
import { useAuth } from '../hooks/useAuth';
import { Logo } from '../components/Logo';
import './AcceptInvite.css';


export function AcceptInvite() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated, checkAuth, logout } = useAuth();
  
  const [inviteDetails, setInviteDetails] = useState<any>(null);
  const [error, setError] = useState('');
  const [isAccepting, setIsAccepting] = useState(false);

  // Auth form state
  const [authMode, setAuthMode] = useState<'login' | 'signup' | 'confirm'>('signup');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [authError, setAuthError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  useEffect(() => {
    // Fetch invite details
    api.get(`/api/v1/invitations/${token}`)
      .then(setInviteDetails)
      .catch(err => setError(err.message));
  }, [token]);

  const handleAuthSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setIsAuthenticating(true);

    try {
      if (authMode === 'signup') {
        try {
          await signOut();
          await signUp({
            username: inviteDetails.email,
            password,
            options: {
              userAttributes: {
                email: inviteDetails.email,
                given_name: firstName || 'User',
                family_name: lastName || 'User',
              }
            }
          });
          setAuthMode('confirm');
          setAuthError('');
        } catch (err: any) {
          if (err.name === 'UsernameExistsException') {
            setAuthMode('login');
            setAuthError('An account with this email already exists. Please sign in.');
          } else {
            throw err;
          }
        }
      } else if (authMode === 'confirm') {
        await confirmSignUp({ username: inviteDetails.email, confirmationCode });
        await signOut();
        await signIn({ username: inviteDetails.email, password });
        await checkAuth();
      } else {
        try {
          await signOut();
          const result = await signIn({ username: inviteDetails.email, password });
          if (result.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
            setAuthMode('confirm');
            setAuthError('Please verify your email address to continue.');
            return;
          }
          await checkAuth();
          // We must check if the auth context successfully updated
          const { fetchAuthSession } = await import('aws-amplify/auth');
          const session = await fetchAuthSession();
          if (!session.tokens?.idToken) {
            throw new Error('Failed to retrieve session tokens after sign in.');
          }
        } catch (err: any) {
          throw err;
        }
      }
    } catch (err: any) {
      setAuthError(err.message || 'Authentication failed');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleAccept = async () => {
    if (!isAuthenticated) {
      setError('You must be logged in to accept this invitation.');
      return;
    }

    setIsAccepting(true);
    try {
      // Clear activeTenantId before accepting so the global API interceptor doesn't attach an X-Tenant-ID
      // header for an organization we don't have access to yet.
      localStorage.removeItem('activeTenantId');
      
      const finalFirstName = firstName || user?.firstName || '';
      const finalLastName = lastName || user?.lastName || '';

      const response = await api.post(`/api/v1/invitations/${token}/accept`, {
        firstName: finalFirstName,
        lastName: finalLastName
      }, {
        headers: { 'X-Tenant-ID': '' }
      });
      // Update active organization to the new one
      if (response.tenantId) {
        localStorage.setItem('activeTenantId', response.tenantId);
      }
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message);
      setIsAccepting(false);
    }
  };

  if (error) {
    return (
      <div className="accept-invite-container">
        <div className="accept-invite-card" style={{ maxWidth: '450px' }}>
          <div className="accept-invite-card__logo"><Logo className="accept-invite-card__logo-mark" /><span>MothNode Dashboard</span></div>
          <h2 className="accept-invite-card__title" style={{ color: 'var(--color-status-critical)' }}>Invalid Invitation</h2>
          <p className="accept-invite-card__description">{error}</p>
          <button onClick={() => navigate('/dashboard')} className="accept-invite-card__button">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (!inviteDetails) {
    return (
      <div className="accept-invite-container">
        <div className="accept-invite-card">
          <p className="accept-invite-card__description">Loading invitation details...</p>
        </div>
      </div>
    );
  }

  const isEmailMismatch = isAuthenticated && user?.email && inviteDetails?.email && user.email.toLowerCase() !== inviteDetails.email.toLowerCase();

  return (
    <div className="accept-invite-container">
      <div className="accept-invite-card">
        <div className="accept-invite-card__logo"><Logo className="accept-invite-card__logo-mark" /><span>MothNode Dashboard</span></div>
        <h2 className="accept-invite-card__title">You've been invited!</h2>
        <p className="accept-invite-card__description">
          {inviteDetails.siteName ? (
            <>
              You have been invited to join the site <strong>{inviteDetails.siteName}</strong> as a <strong>{inviteDetails.role}</strong>
              {inviteDetails.tenantName ? ` under the organization ${inviteDetails.tenantName}` : ''}.
            </>
          ) : (
            <>
              You have been invited to join <strong>{inviteDetails.tenantName}</strong> as a <strong>{inviteDetails.role}</strong>.
            </>
          )}
        </p>

        {isAuthenticated ? (
          isEmailMismatch ? (
            <div className="accept-invite-card__auth-box">
              <p className="accept-invite-card__error-banner" style={{ borderLeft: 'none', background: 'transparent', padding: 0 }}>
                You are currently signed in as <strong>{user.email}</strong>, but this invitation was sent to <strong>{inviteDetails.email}</strong>.
              </p>
              <button 
                className="accept-invite-card__button" 
                onClick={() => logout()}
              >
                Sign Out
              </button>
            </div>
          ) : (
            <button 
              className="accept-invite-card__button" 
              onClick={handleAccept} 
              disabled={isAccepting}
            >
              {isAccepting ? 'Accepting...' : 'Accept Invitation'}
            </button>
          )
        ) : (
          <div className="accept-invite-card__auth-box">
            <h3 className="accept-invite-card__auth-title">
              {authMode === 'signup' && 'Create an account to accept'}
              {authMode === 'login' && 'Sign in to accept'}
              {authMode === 'confirm' && 'Verify your email'}
            </h3>
            
            {authError && (
              <div className="accept-invite-card__error-banner">
                {authError}
              </div>
            )}

            {authMode === 'confirm' && (
              <p className="accept-invite-card__info-text">
                We sent a verification code to <strong>{inviteDetails.email}</strong>. Please enter it below.
              </p>
            )}

            <form onSubmit={handleAuthSubmit} className="accept-invite-card__form">
              {authMode !== 'confirm' && (
                <>
                  <div className="accept-invite-card__field">
                    <label className="accept-invite-card__label">Email</label>
                    <input 
                      type="email" 
                      value={inviteDetails.email} 
                      disabled 
                      className="accept-invite-card__input"
                    />
                  </div>

                  {authMode === 'signup' && (
                    <div className="accept-invite-card__row">
                      <div className="accept-invite-card__col">
                        <label className="accept-invite-card__label">First Name</label>
                        <input 
                          type="text" 
                          value={firstName} 
                          onChange={e => setFirstName(e.target.value)}
                          required 
                          className="accept-invite-card__input"
                        />
                      </div>
                      <div className="accept-invite-card__col">
                        <label className="accept-invite-card__label">Last Name</label>
                        <input 
                          type="text" 
                          value={lastName} 
                          onChange={e => setLastName(e.target.value)}
                          required 
                          className="accept-invite-card__input"
                        />
                      </div>
                    </div>
                  )}

                  <div className="accept-invite-card__field">
                    <label className="accept-invite-card__label">Password</label>
                    <input 
                      type="password" 
                      value={password} 
                      onChange={e => setPassword(e.target.value)}
                      required 
                      minLength={8}
                      className="accept-invite-card__input"
                    />
                  </div>

                  {authMode === 'signup' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px', marginBottom: '8px' }}>
                      <input 
                        type="checkbox" 
                        id="acceptTerms" 
                        required 
                        style={{ cursor: 'pointer', width: 'auto', height: 'auto', margin: 0 }}
                      />
                      <label htmlFor="acceptTerms" style={{ fontSize: '14px', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
                        I agree to the <a href="/terms" target="_blank" rel="noreferrer" style={{ color: 'var(--color-brand-primary)', textDecoration: 'none' }}>Terms of Service</a> and <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--color-brand-primary)', textDecoration: 'none' }}>Privacy Policy</a>.
                      </label>
                    </div>
                  )}
                </>
              )}

              {authMode === 'confirm' && (
                <div className="accept-invite-card__field">
                  <label className="accept-invite-card__label">Verification Code</label>
                  <input 
                    type="text" 
                    value={confirmationCode} 
                    onChange={e => setConfirmationCode(e.target.value)}
                    required 
                    className="accept-invite-card__input"
                  />
                </div>
              )}

              <button 
                type="submit"
                className="accept-invite-card__button" 
                disabled={isAuthenticating}
              >
                {isAuthenticating ? 'Please wait...' : (authMode === 'signup' ? 'Create Account & Continue' : authMode === 'login' ? 'Sign In & Continue' : 'Verify & Continue')}
              </button>
            </form>

            <div className="accept-invite-card__footer">
              {authMode === 'signup' && (
                <>Already have an account? <a href="#" onClick={(e) => { e.preventDefault(); setAuthMode('login'); setAuthError(''); }} className="accept-invite-card__footer-link">Sign In</a></>
              )}
              {authMode === 'login' && (
                <>Don't have an account? <a href="#" onClick={(e) => { e.preventDefault(); setAuthMode('signup'); setAuthError(''); }} className="accept-invite-card__footer-link">Create one</a></>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AcceptInvite;
